import { Spinner } from "@primer/react";
import { useMemo, useState } from "react";
import { authApi } from "../api/auth/authApi";
import { evaluatePasswordStrength } from "../helpers/passwordStrength";

export interface IChangePasswordResult {
    message: string;
    access_token?: string;
    expires_at?: string;
}

interface IProps {
    /** Mã SV — để check "mật khẩu khác mã SV" (khớp rule backend). */
    maSv?: string;
    /** Gọi khi đổi mật khẩu thành công (parent quyết định toast + thay token). */
    onSuccess: (result: IChangePasswordResult) => void;
    /** Có truyền → hiện nút "Hủy". Bỏ trống (màn hình buộc đổi) → không cho hủy. */
    onCancel?: () => void;
    autoFocusOld?: boolean;
    /** Nhãn nút submit. Mặc định "Xác nhận đổi mật khẩu". */
    submitLabel?: string;
}

/** 3 mức độ mạnh → nhãn + màu cho thanh đo. */
const STRENGTH_META = [
    { label: "", color: "#e2e5ef" },
    { label: "Yếu", color: "#ef4444" },
    { label: "Trung bình", color: "#f59e0b" },
    { label: "Mạnh", color: "#22c55e" },
];

/**
 * Form đổi mật khẩu dùng chung cho 2 nơi:
 *   - Modal đổi mật khẩu tự nguyện ở Header (ChangePasswordDialog).
 *   - Màn hình BUỘC đổi khi mật khẩu yếu (ForcePasswordChange) — không có onCancel.
 *
 * Bắt buộc mật khẩu MỚI phải MẠNH (khớp backend): >=8 ký tự, >=3/4 nhóm ký tự, khác mã SV.
 * Thanh đo + checklist realtime gợi ý SV; nút xác nhận disable tới khi đủ mạnh + khớp nhập lại.
 * Style scoped trong .cpf để đẹp đồng nhất ở cả modal lẫn màn buộc đổi (không phụ thuộc CSS trang cha).
 */
export const ChangePasswordForm = ({
    maSv,
    onSuccess,
    onCancel,
    autoFocusOld = true,
    submitLabel = "Xác nhận đổi mật khẩu",
}: IProps) => {
    const [oldPass, setOldPass] = useState("");
    const [newPass, setNewPass] = useState("");
    const [rePass, setRePass] = useState("");
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showRe, setShowRe] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const strength = useMemo(() => evaluatePasswordStrength(newPass, maSv), [newPass, maSv]);
    const reMatches = rePass.length > 0 && newPass === rePass;
    const canSubmit =
        !isSubmitting && !!oldPass && strength.isStrong && reMatches && newPass !== oldPass;

    // Mức độ mạnh 0..3 (0 = chưa nhập). isStrong → Mạnh; đạt >=2 tiêu chí → Trung bình; còn lại Yếu.
    const passedCount = strength.checks.filter((c) => c.passed).length;
    const level = newPass.length === 0 ? 0 : strength.isStrong ? 3 : passedCount >= 2 ? 2 : 1;

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!oldPass) return setErrorMsg("Vui lòng nhập mật khẩu hiện tại.");
        if (!newPass) return setErrorMsg("Vui lòng nhập mật khẩu mới.");
        if (newPass === oldPass) return setErrorMsg("Mật khẩu mới phải khác mật khẩu cũ.");
        if (!strength.isStrong) {
            const fail = strength.checks.find((c) => !c.passed);
            return setErrorMsg(fail ? `Mật khẩu mới chưa đạt: ${fail.label}.` : "Mật khẩu mới chưa đủ mạnh.");
        }
        if (newPass !== rePass) return setErrorMsg("Nhập lại mật khẩu mới chưa khớp.");

        setErrorMsg("");
        setIsSubmitting(true);
        const res = await authApi.ChangePassword({
            mat_khau_cu: oldPass,
            mat_khau_moi: newPass,
            mat_khau_moi_nhap_lai: rePass,
        });
        setIsSubmitting(false);
        if (res.is_success) {
            onSuccess({
                message: res.data?.message || "Đổi mật khẩu thành công.",
                access_token: res.data?.access_token,
                expires_at: res.data?.expires_at,
            });
        } else {
            setErrorMsg(res.message || "Đổi mật khẩu thất bại. Vui lòng thử lại.");
        }
    };

    const renderField = (
        label: string,
        value: string,
        onChange: (v: string) => void,
        show: boolean,
        toggleShow: () => void,
        autoFocus = false
    ) => (
        <div className="cpf-group">
            <label className="cpf-label">
                {label} <span>*</span>
            </label>
            <div className="cpf-field">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={isSubmitting}
                    autoFocus={autoFocus}
                    autoComplete="off"
                />
                <button
                    type="button"
                    className="cpf-eye"
                    onClick={toggleShow}
                    tabIndex={-1}
                    aria-label={show ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                >
                    <i className={`fa ${show ? "fa-eye" : "fa-eye-slash"}`} aria-hidden="true" />
                </button>
            </div>
        </div>
    );

    return (
        <form
            className="cpf"
            onSubmit={handleSubmit}
            onKeyUp={(e) => {
                if (e.key === "Enter" && canSubmit) handleSubmit();
            }}
        >
            <style>{`
                .cpf .cpf-group { margin-bottom: 14px; }
                .cpf .cpf-label { display:block; margin-bottom:6px; font-size:13px; font-weight:600; color:#3a3f52; }
                .cpf .cpf-label span { color:#ef4444; }
                .cpf .cpf-field { position:relative; }
                .cpf .cpf-field input {
                    width:100%; box-sizing:border-box; height:44px; padding:0 44px 0 14px;
                    border:1.5px solid #e4e7f0; border-radius:12px; background:#fafbff;
                    font-size:14px; color:#1f2430; font-family:inherit; outline:none;
                    transition:border-color .15s ease, box-shadow .15s ease, background .15s ease;
                }
                .cpf .cpf-field input::placeholder { color:#b3b8c9; }
                .cpf .cpf-field input:focus { border-color:#4aa3ff; background:#fff; box-shadow:0 0 0 4px rgba(0,123,255,.15); }
                .cpf .cpf-field input:disabled { background:#f2f3f8; cursor:not-allowed; }
                .cpf .cpf-eye {
                    position:absolute; right:6px; top:50%; transform:translateY(-50%);
                    width:34px; height:34px; display:flex; align-items:center; justify-content:center;
                    background:transparent; border:none; cursor:pointer; color:#9aa0b4; border-radius:8px;
                    transition:color .15s ease, background .15s ease;
                }
                .cpf .cpf-eye:hover { color:#007bff; background:#eaf3ff; }

                .cpf .cpf-meter { display:flex; align-items:center; gap:10px; margin:2px 0 12px; }
                .cpf .cpf-meter-track { flex:1; display:flex; gap:5px; }
                .cpf .cpf-meter-seg { flex:1; height:6px; border-radius:99px; background:#e9ebf3; transition:background .25s ease; }
                .cpf .cpf-meter-label { font-size:12px; font-weight:700; min-width:64px; text-align:right; }

                .cpf .cpf-checks { margin:0 0 14px; padding:12px 14px; background:#f7f8fc; border-radius:12px; display:grid; gap:6px; }
                .cpf .cpf-check { display:flex; align-items:center; gap:9px; font-size:12.5px; line-height:1.35; transition:color .2s ease; }
                .cpf .cpf-check i { width:15px; text-align:center; font-size:12px; }
                .cpf .cpf-check.ok { color:#15a34a; }
                .cpf .cpf-check.no { color:#8b90a3; }

                .cpf .cpf-alert { background:#fef2f2; border:1px solid #fecaca; color:#b42318; border-radius:10px; padding:10px 13px; font-size:13px; margin-bottom:12px; }

                .cpf .cpf-actions { display:flex; flex-direction:column; gap:10px; margin-top:4px; }
                .cpf .cpf-btn-primary {
                    width:100%; height:46px; border:none; border-radius:12px; cursor:pointer;
                    font-size:15px; font-weight:700; color:#fff; font-family:inherit;
                    background:linear-gradient(135deg,#1f8fff 0%,#007bff 100%);
                    box-shadow:0 8px 20px rgba(0,123,255,.32);
                    display:flex; align-items:center; justify-content:center; gap:8px;
                    transition:transform .1s ease, box-shadow .2s ease, opacity .2s ease;
                }
                .cpf .cpf-btn-primary:hover:not(:disabled) { box-shadow:0 10px 26px rgba(0,123,255,.45); }
                .cpf .cpf-btn-primary:active:not(:disabled) { transform:translateY(1px); }
                .cpf .cpf-btn-primary:disabled { opacity:.5; cursor:not-allowed; box-shadow:none; }
                .cpf .cpf-btn-ghost {
                    width:100%; height:42px; border:1px solid #e4e7f0; border-radius:12px; cursor:pointer;
                    font-size:14px; font-weight:600; color:#5a6072; background:#fff; font-family:inherit;
                    transition:background .15s ease, border-color .15s ease;
                }
                .cpf .cpf-btn-ghost:hover:not(:disabled) { background:#f6f7fb; border-color:#d3d7e4; }
                .cpf .cpf-btn-ghost:disabled { opacity:.5; cursor:not-allowed; }
            `}</style>

            {renderField("Mật khẩu hiện tại", oldPass, setOldPass, showOld, () => setShowOld((v) => !v), autoFocusOld)}
            {renderField("Mật khẩu mới", newPass, setNewPass, showNew, () => setShowNew((v) => !v))}

            {/* Thanh đo độ mạnh */}
            {newPass.length > 0 && (
                <div className="cpf-meter">
                    <div className="cpf-meter-track">
                        {[1, 2, 3].map((seg) => (
                            <div
                                key={seg}
                                className="cpf-meter-seg"
                                style={{ background: seg <= level ? STRENGTH_META[level].color : undefined }}
                            />
                        ))}
                    </div>
                    <span className="cpf-meter-label" style={{ color: STRENGTH_META[level].color }}>
                        {STRENGTH_META[level].label}
                    </span>
                </div>
            )}

            {/* Checklist tiêu chí */}
            {newPass.length > 0 && (
                <div className="cpf-checks">
                    {strength.checks.map((c) => (
                        <div key={c.key} className={`cpf-check ${c.passed ? "ok" : "no"}`}>
                            <i className={`fa ${c.passed ? "fa-check-circle" : "fa-circle-o"}`} aria-hidden="true" />
                            {c.label}
                        </div>
                    ))}
                </div>
            )}

            {renderField("Nhập lại mật khẩu mới", rePass, setRePass, showRe, () => setShowRe((v) => !v))}

            {errorMsg && <div className="cpf-alert">{errorMsg}</div>}

            <div className="cpf-actions">
                <button type="submit" className="cpf-btn-primary" disabled={!canSubmit}>
                    {isSubmitting ? <Spinner size="small" /> : submitLabel}
                </button>
                {onCancel && (
                    <button type="button" className="cpf-btn-ghost" onClick={onCancel} disabled={isSubmitting}>
                        Hủy
                    </button>
                )}
            </div>
        </form>
    );
};

export default ChangePasswordForm;
